import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { Archive, Check, Copy, Pencil, Plus, RotateCcw, UserRound, X } from 'lucide-react';
import { api, ApiClientError } from '../api';
import { useAuth } from '../auth';
import type { Child } from '../types';
import { Loading } from './Loading';
import { useToast } from './Toast';

const avatars = [
  ['dino', '🦕'],
  ['lion', '🦁'],
  ['fox', '🦊'],
  ['panda', '🐼'],
  ['rabbit', '🐰'],
  ['rocket', '🚀']
] as const;

function avatarEmoji(key: string): string {
  return avatars.find(([value]) => value === key)?.[1] ?? '🦕';
}

const emptyForm = {
  nickname: '',
  birthYear: '',
  targetPoints: 1000,
  avatarKey: 'dino',
  copyCurrent: true
};

export function ChildProfilesPanel() {
  const { selectedChild, setSelectedChildId, refresh } = useAuth();
  const { show } = useToast();
  const [profiles, setProfiles] = useState<Child[]>([]);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState<Child | null>(null);
  const [archiveCandidate, setArchiveCandidate] = useState<Child | null>(null);
  const [busy, setBusy] = useState('');
  const [loading, setLoading] = useState(true);

  const loadProfiles = useCallback(async () => {
    try {
      const rows = await api<Child[]>('/children?includeArchived=1', { omitChildContext: true });
      setProfiles(rows);
    } catch (error) {
      show(error instanceof ApiClientError ? error.message : 'Không thể tải hồ sơ các bé.', 'error');
    } finally {
      setLoading(false);
    }
  }, [show]);

  useEffect(() => {
    setLoading(true);
    void loadProfiles();
  }, [loadProfiles]);

  const activeProfiles = useMemo(
    () => profiles.filter((child) => child.active !== false),
    [profiles]
  );
  const archivedProfiles = useMemo(
    () => profiles.filter((child) => child.active === false),
    [profiles]
  );
  const canArchive = activeProfiles.length > 1;
  const copySource = useMemo(
    () => activeProfiles.find((child) => child.id === selectedChild?.id) ?? activeProfiles[0] ?? null,
    [activeProfiles, selectedChild?.id]
  );

  async function addChild(event: FormEvent) {
    event.preventDefault();
    setBusy('create');
    try {
      const created = await api<Child>('/children', {
        method: 'POST',
        omitChildContext: true,
        body: {
          nickname: form.nickname,
          birthYear: form.birthYear ? Number(form.birthYear) : null,
          targetPoints: Number(form.targetPoints),
          avatarKey: form.avatarKey,
          copyFromChildId: form.copyCurrent ? copySource?.id ?? null : null
        }
      });
      await refresh();
      setSelectedChildId(created.id);
      setForm(emptyForm);
      await loadProfiles();
      show(`Đã tạo hồ sơ ${created.nickname}.`, 'success');
    } catch (error) {
      show(error instanceof ApiClientError ? error.message : 'Không thể thêm hồ sơ bé.', 'error');
    } finally {
      setBusy('');
    }
  }

  function startEdit(child: Child) {
    setEditing({ ...child });
    setArchiveCandidate(null);
  }

  async function saveEdit(event: FormEvent) {
    event.preventDefault();
    if (!editing) return;
    setBusy(`edit:${editing.id}`);
    try {
      const updated = await api<Child>(`/children/${encodeURIComponent(editing.id)}`, {
        method: 'PATCH',
        omitChildContext: true,
        body: {
          nickname: editing.nickname,
          birthYear: editing.birthYear,
          targetPoints: Number(editing.targetPoints),
          avatarKey: editing.avatarKey
        }
      });
      await refresh();
      await loadProfiles();
      show(`Đã cập nhật hồ sơ ${updated.nickname}.`, 'success');
      setEditing(null);
    } catch (error) {
      show(error instanceof ApiClientError ? error.message : 'Không thể cập nhật hồ sơ.', 'error');
    } finally {
      setBusy('');
    }
  }

  async function archiveChild(child: Child) {
    setBusy(`archive:${child.id}`);
    try {
      const result = await api<{ nextChildId: string }>(`/children/${encodeURIComponent(child.id)}`, {
        method: 'DELETE',
        omitChildContext: true
      });
      setSelectedChildId(result.nextChildId);
      await refresh();
      await loadProfiles();
      show(`Đã gỡ hồ sơ ${child.nickname}; toàn bộ lịch sử vẫn được lưu an toàn.`, 'success');
      setArchiveCandidate(null);
    } catch (error) {
      show(error instanceof ApiClientError ? error.message : 'Không thể gỡ hồ sơ.', 'error');
    } finally {
      setBusy('');
    }
  }

  async function restoreChild(child: Child) {
    setBusy(`restore:${child.id}`);
    try {
      const restored = await api<Child>(`/children/${encodeURIComponent(child.id)}/restore`, {
        method: 'POST',
        omitChildContext: true
      });
      await refresh();
      setSelectedChildId(restored.id);
      await loadProfiles();
      show(`Đã khôi phục hồ sơ ${restored.nickname}.`, 'success');
    } catch (error) {
      show(error instanceof ApiClientError ? error.message : 'Không thể khôi phục hồ sơ.', 'error');
    } finally {
      setBusy('');
    }
  }

  if (loading) return <Loading label="Đang tải hồ sơ các bé…" />;

  return (
    <div className="child-profiles-stack">
      <form className="surface manage-form child-create-form" onSubmit={addChild}>
        <div className="section-heading">
          <div><span className="section-icon section-icon--blue"><Plus size={19} /></span><h2>Thêm hồ sơ bé</h2></div>
        </div>
        <p className="form-help">Mỗi hồ sơ có nhiệm vụ, điểm, catalog quà, ngân sách, sở thích, lịch nhắc và gợi ý AI hoàn toàn riêng.</p>
        <div className="form-grid form-grid--compact">
          <label>Tên gọi của bé<input required maxLength={50} value={form.nickname} onChange={(event) => setForm({ ...form, nickname: event.target.value })} placeholder="Ví dụ: Bảo Anh" /></label>
          <label>Năm sinh<input type="number" min="1990" max={new Date().getFullYear()} value={form.birthYear} onChange={(event) => setForm({ ...form, birthYear: event.target.value })} placeholder="2018" /></label>
          <label>Mục tiêu điểm<input required type="number" min="10" max="1000000" value={form.targetPoints} onChange={(event) => setForm({ ...form, targetPoints: Number(event.target.value) })} /></label>
          <label>Ảnh đại diện<select value={form.avatarKey} onChange={(event) => setForm({ ...form, avatarKey: event.target.value })}>{avatars.map(([value, emoji]) => <option key={value} value={value}>{emoji} {value}</option>)}</select></label>
        </div>
        {copySource && (
          <label className="copy-workspace-card">
            <input type="checkbox" checked={form.copyCurrent} onChange={(event) => setForm({ ...form, copyCurrent: event.target.checked })} />
            <Copy size={19} />
            <span><strong>Sao chép thiết lập từ {copySource.nickname}</strong><small>Chỉ sao chép nhiệm vụ, catalog quà và quy tắc thành các bản độc lập; không sao chép điểm, lịch sử, sở thích hay wishlist.</small></span>
          </label>
        )}
        <button className="button button--primary" disabled={Boolean(busy)} type="submit"><Plus size={17} /> {busy === 'create' ? 'Đang tạo…' : 'Thêm hồ sơ bé'}</button>
      </form>

      <section className="surface">
        <div className="section-heading">
          <div><span className="section-icon section-icon--purple"><UserRound size={19} /></span><h2>Các hồ sơ trong gia đình</h2></div>
          <span>{activeProfiles.length} đang dùng</span>
        </div>
        <div className="child-profile-grid">
          {activeProfiles.map((child) => {
            const active = child.id === selectedChild?.id;
            const isEditing = editing?.id === child.id;
            const confirmingArchive = archiveCandidate?.id === child.id;
            return (
              <article key={child.id} className={`child-profile-card ${active ? 'is-active' : ''}`}>
                {isEditing && editing ? (
                  <form onSubmit={saveEdit} className="child-edit-form">
                    <div className="child-card-heading"><span className="child-avatar-large">{avatarEmoji(editing.avatarKey)}</span><strong>Sửa hồ sơ</strong></div>
                    <label>Tên gọi<input required value={editing.nickname} onChange={(event) => setEditing({ ...editing, nickname: event.target.value })} /></label>
                    <div className="form-grid form-grid--compact">
                      <label>Năm sinh<input type="number" min="1990" max={new Date().getFullYear()} value={editing.birthYear ?? ''} onChange={(event) => setEditing({ ...editing, birthYear: event.target.value ? Number(event.target.value) : null })} /></label>
                      <label>Mục tiêu điểm<input type="number" min="10" value={editing.targetPoints} onChange={(event) => setEditing({ ...editing, targetPoints: Number(event.target.value) })} /></label>
                      <label>Đại diện<select value={editing.avatarKey} onChange={(event) => setEditing({ ...editing, avatarKey: event.target.value })}>{avatars.map(([value, emoji]) => <option key={value} value={value}>{emoji} {value}</option>)}</select></label>
                    </div>
                    <div className="inline-actions">
                      <button type="submit" className="button button--primary button--small" disabled={Boolean(busy)}><Check size={16} /> Lưu</button>
                      <button type="button" className="button button--soft button--small" onClick={() => setEditing(null)}><X size={16} /> Hủy</button>
                    </div>
                  </form>
                ) : confirmingArchive ? (
                  <div className="archive-confirmation">
                    <strong>Gỡ hồ sơ {child.nickname}?</strong>
                    <p>Điểm, nhiệm vụ, quà, sở thích và lịch sử không bị xóa. Bạn có thể khôi phục hồ sơ sau này.</p>
                    <div className="inline-actions">
                      <button type="button" className="button button--danger button--small" disabled={Boolean(busy)} onClick={() => void archiveChild(child)}><Archive size={16} /> Xác nhận gỡ</button>
                      <button type="button" className="button button--soft button--small" onClick={() => setArchiveCandidate(null)}>Hủy</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <button type="button" className="child-card-main" onClick={() => setSelectedChildId(child.id)}>
                      <span className="child-avatar-large">{avatarEmoji(child.avatarKey)}</span>
                      <span><strong>{child.nickname}</strong><small>{child.birthYear ? `Sinh năm ${child.birthYear}` : 'Chưa ghi năm sinh'} · Mục tiêu {child.targetPoints.toLocaleString('vi-VN')} điểm</small></span>
                      {active && <span className="active-profile-badge"><Check size={14} /> Đang dùng</span>}
                    </button>
                    <div className="child-card-actions">
                      <button type="button" className="button button--soft button--small" onClick={() => startEdit(child)}><Pencil size={15} /> Sửa</button>
                      <button type="button" className="button button--ghost-danger button--small" disabled={!canArchive} title={!canArchive ? 'Gia đình phải có ít nhất một hồ sơ bé.' : undefined} onClick={() => setArchiveCandidate(child)}><Archive size={15} /> Gỡ</button>
                    </div>
                  </>
                )}
              </article>
            );
          })}
        </div>
      </section>

      {archivedProfiles.length > 0 && (
        <section className="surface archived-profile-section">
          <div className="section-heading">
            <div><span className="section-icon section-icon--orange"><Archive size={18} /></span><h2>Hồ sơ đã gỡ</h2></div>
            <span>{archivedProfiles.length} hồ sơ</span>
          </div>
          <div className="archived-profile-list">
            {archivedProfiles.map((child) => (
              <div className="archived-profile-row" key={child.id}>
                <span className="child-avatar-small">{avatarEmoji(child.avatarKey)}</span>
                <div><strong>{child.nickname}</strong><small>Dữ liệu và lịch sử vẫn được giữ nguyên.</small></div>
                <button className="button button--soft button--small" type="button" disabled={Boolean(busy)} onClick={() => void restoreChild(child)}><RotateCcw size={15} /> Khôi phục</button>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
